/**
 * The microphone panel (SEO_BRIEF_08 section 2): tools/_mic_panel.html's markup driven by
 * lib/recorder.ts (Record) and lib/live-speech.ts (Live). One component, mounted by pages/mic.ts on
 * the five panel pages and by pages/tool.ts on /free-transcription, which hand it the one thing
 * that leaves the browser: `transcribe`, B6's upload (lib/upload-offer.ts `send`).
 *
 * WHAT THE COPY PROMISES, AND WHERE IT IS KEPT (section 2.3):
 *   - Record, playback and "Download recording" stay in this tab. The Blob is played from a
 *     `blob:` URL and saved with an anchor; `transcribe` is called from the Transcribe button and
 *     from nowhere else.
 *   - Live typing makes no request of ours: the browser's recognition does the listening, and the
 *     text is copied or saved as a .txt made here.
 *   - A Live-first page reads the minutes meter only when Record is opened (`data-mic-defer-quota`,
 *     boot.ts), so Live alone asks our server nothing.
 *
 * THE USAGE COUNTER (lib/usage.ts) hears `record_start`, `record_transcribe` (once the server took
 * the recording), `record_download`, `live_start` and `live_copy` (the Copy button's every press,
 * and words selected in the box and copied, once per page view), under the page's slug.
 *
 * Every word shown is the template's: the status lines are read from `[data-mic-label]` by key.
 */

import { copyText } from './clipboard.ts';
import { watchCopies } from './copies.ts';
import { qs, qsa } from './dom.ts';
import { pageLang } from './i18n.ts';
import {
  appendFinal,
  createLive,
  interimTail,
  type Live,
  type LiveFailure,
  pickLanguage,
  type RecognitionCtor,
  recognitionCtor,
  startLanguage,
} from './live-speech.ts';
import {
  createRecorder,
  extensionFor,
  formatClock,
  type LevelReader,
  RECORD_CAP_S,
  type Recorder,
  type RecorderEnv,
  type RecorderFailure,
  type RecorderState,
  type Recording,
  recordingName,
  WARN_FROM_S,
  type WakeLockLike,
} from './recorder.ts';
import { countUse, type MicEvent, recordingFormat, TOOL_SLUG } from './usage.ts';

export type MicMode = 'live' | 'record';

/** The box's styles its mirror copies, so the mirror's words wrap exactly where the box's do. */
const MIRRORED = [
  'boxSizing',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'lineHeight',
  'letterSpacing',
  'wordSpacing',
  'textTransform',
  'textIndent',
  'textAlign',
  'tabSize',
  'direction',
] as const;

export interface MicPanelDeps {
  /**
   * Send the recording down B6. `counted` is called once the server has taken it. Resolves true
   * then, false when it was refused (the page shows why).
   */
  transcribe(file: File, seconds: number, counted: () => void): Promise<boolean>;
  /** Read the minutes meter, on a page that waits for Record to open before it does. */
  loadQuota?(): void;
  /** The browser's pieces, replaceable in tests. */
  recognition?: RecognitionCtor | null;
  recorderEnv?: RecorderEnv;
  /** The browser's language, for Live's default. */
  language?: string;
}

export interface MicPanel {
  open(mode: MicMode): void;
  readonly mode: MicMode;
  readonly recorder: Recorder;
  readonly live: Live | null;
}

/* ── the browser's pieces ───────────────────────────────────────────────────── */

type WakeLockNavigator = Navigator & {
  wakeLock?: { request(type: 'screen'): Promise<WakeLockLike> };
};

/** The microphone's loudness from Web Audio, in decibels mapped to 0..1 (-60 dB to 0 dB). */
function webAudioLevel(stream: MediaStream): LevelReader | null {
  const scope = globalThis as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const Ctx = scope.AudioContext ?? scope.webkitAudioContext;
  if (!Ctx) return null;
  try {
    const ctx = new Ctx();
    void ctx.resume?.().catch(() => undefined);
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    return {
      read() {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += v * v;
        const rms = Math.sqrt(sum / buf.length);
        if (rms <= 0) return 0;
        const db = 20 * Math.log10(rms);
        return Math.max(0, Math.min(1, (db + 60) / 60));
      },
      close() {
        try {
          source.disconnect();
        } catch {
          /* already disconnected */
        }
        void ctx.close().catch(() => undefined);
      },
    };
  } catch {
    return null;
  }
}

/** The recorder's environment in a real browser. */
export function browserRecorderEnv(): RecorderEnv {
  const nav = (typeof navigator === 'undefined' ? undefined : navigator) as
    | WakeLockNavigator
    | undefined;
  const devices = nav?.mediaDevices;
  const scope = globalThis as unknown as { MediaRecorder?: RecorderEnv['MediaRecorder'] };
  const wakeLock = nav?.wakeLock;
  return {
    getUserMedia:
      devices && typeof devices.getUserMedia === 'function'
        ? (c) => devices.getUserMedia(c)
        : undefined,
    MediaRecorder: scope.MediaRecorder,
    now: () => performance.now(),
    setInterval: (fn, ms) => setInterval(fn, ms),
    clearInterval: (h) => clearInterval(h as ReturnType<typeof setInterval>),
    requestWakeLock: wakeLock ? () => wakeLock.request('screen') : undefined,
    onVisible: (fn) => {
      const listener = () => {
        if (document.visibilityState === 'visible') fn();
      };
      document.addEventListener('visibilitychange', listener);
      return () => document.removeEventListener('visibilitychange', listener);
    },
    levelReader: webAudioLevel,
  };
}

/* ── the panel ─────────────────────────────────────────────────────────────── */

export function mountMicPanel(root: HTMLElement, deps: MicPanelDeps): MicPanel {
  const toolAttr = root.dataset['usageTool'] ?? '';
  const tool = TOOL_SLUG.test(toolAttr) ? toolAttr : '';
  const deferQuota = root.hasAttribute('data-mic-defer-quota');
  let quotaRead = !deferQuota;

  const count = (event: MicEvent, format: Parameters<typeof countUse>[2]) => {
    if (tool) countUse(event, tool, format);
  };

  /** A status line's words, from the template, with `time` filled in where it has a slot. */
  function label(key: string, time?: string): DocumentFragment | null {
    const source = qs<HTMLElement>(`[data-mic-label="${key}"]`, root);
    if (!source) return null;
    const frag = document.createDocumentFragment();
    for (const node of Array.from(source.childNodes)) frag.append(node.cloneNode(true));
    if (time !== undefined) {
      for (const slot of Array.from(frag.querySelectorAll<HTMLElement>('[data-slot="time"]'))) {
        slot.textContent = time;
      }
    }
    return frag;
  }

  function say(line: HTMLElement | null, key: string | null, time?: string): void {
    if (!line) return;
    const frag = key ? label(key, time) : null;
    line.replaceChildren(...(frag ? [frag] : []));
  }

  /* ── tabs ──────────────────────────────────────────────────────────── */

  const tabs = qsa<HTMLButtonElement>('[data-mic-tab]', root);
  const panes = new Map<MicMode, HTMLElement>();
  for (const pane of qsa<HTMLElement>('[data-mic-pane]', root)) {
    const m = pane.dataset['micPane'];
    if (m === 'live' || m === 'record') panes.set(m, pane);
  }
  let mode: MicMode = root.dataset['micOpen'] === 'live' && panes.has('live') ? 'live' : 'record';

  function open(next: MicMode): void {
    if (!panes.has(next)) return;
    mode = next;
    for (const [m, pane] of panes) pane.hidden = m !== next;
    for (const tab of tabs) {
      const selected = tab.dataset['micTab'] === next;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    }
    if (next === 'record') {
      // Two listeners on one microphone helps nobody: Record stops Live.
      live?.stop();
      if (!quotaRead) {
        quotaRead = true;
        deps.loadQuota?.();
      }
    }
  }

  for (const tab of tabs) {
    tab.addEventListener('click', () => {
      const m = tab.dataset['micTab'];
      if ((m === 'live' || m === 'record') && !tab.disabled) open(m);
    });
    tab.addEventListener('keydown', (ev) => {
      if (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') return;
      const enabled = tabs.filter((t) => !t.disabled && !t.hidden);
      const i = enabled.indexOf(tab);
      const nextTab =
        enabled[(i + (ev.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length];
      const m = nextTab?.dataset['micTab'];
      if (nextTab && (m === 'live' || m === 'record')) {
        open(m);
        nextTab.focus();
      }
    });
  }
  for (const link of qsa<HTMLButtonElement>('[data-mic-switch]', root)) {
    link.addEventListener('click', () => {
      const m = link.dataset['micSwitch'];
      if (m === 'live' || m === 'record') {
        open(m);
        qs<HTMLElement>(m === 'live' ? '[data-live-toggle]' : '[data-rec-start]', root)?.focus();
      }
    });
  }

  /* ── Live ──────────────────────────────────────────────────────────── */

  const Recognition =
    deps.recognition !== undefined ? deps.recognition : recognitionCtor(globalThis);
  let live: Live | null = null;
  const livePane = panes.get('live');

  if (livePane && !Recognition) {
    // Firefox, and any browser without speech recognition: no Live, Record open, one line why.
    livePane.remove();
    panes.delete('live');
    for (const tab of tabs) if (tab.dataset['micTab'] === 'live') tab.hidden = true;
    qs<HTMLElement>('[data-mic-tabs]', root)?.setAttribute('hidden', '');
    for (const link of qsa<HTMLElement>('[data-mic-switch]', root)) link.parentElement?.remove();
    const note = qs<HTMLElement>('[data-mic-no-live]', root);
    if (note) note.hidden = false;
    mode = 'record';
  } else if (livePane && Recognition) {
    const toggle = qs<HTMLButtonElement>('[data-live-toggle]', livePane);
    const langSelect = qs<HTMLSelectElement>('[data-live-lang]', livePane);
    const status = qs<HTMLElement>('[data-live-status]', livePane);
    const text = qs<HTMLTextAreaElement>('[data-live-text]', livePane);
    const interim = qs<HTMLElement>('[data-live-interim]', livePane);
    const mirror = qs<HTMLElement>('[data-live-mirror]', livePane);
    const said = qs<HTMLElement>('[data-live-said]', livePane);
    const copyBtn = qs<HTMLButtonElement>('[data-live-copy]', livePane);
    const saveBtn = qs<HTMLButtonElement>('[data-live-download]', livePane);
    const clearBtn = qs<HTMLButtonElement>('[data-live-clear]', livePane);

    if (langSelect) {
      const offered = Array.from(langSelect.options, (o) => o.value);
      const browser =
        deps.language ?? (typeof navigator === 'undefined' ? undefined : navigator.language);
      langSelect.value = pickLanguage(startLanguage(pageLang(), browser), offered);
    }

    const syncButtons = () => {
      const empty = !text || text.value.trim() === '';
      for (const b of [copyBtn, saveBtn, clearBtn]) if (b) b.disabled = empty;
    };
    text?.addEventListener('input', syncButtons);

    // The guess shows grey inside the box, right after the last word: a see-through copy of the
    // box's words is laid over it with the guess at its end, and the box grows to fit both, so the
    // two never scroll apart.
    let guess = '';
    let listening = false;
    const paint = () => {
      if (!text || !mirror || !said || !interim) return;
      // The zero-width space holds a trailing line break's empty line open, as the box does.
      said.textContent = `${text.value}​`;
      interim.textContent = interimTail(text.value, guess);
      if (!text.offsetWidth) return; // a hidden pane: sized when it shows
      const box = getComputedStyle(text);
      for (const prop of MIRRORED) mirror.style[prop] = box[prop];
      text.style.height = `${mirror.offsetHeight}px`;
      text.scrollTop = 0;
      if (listening && guess) interim.scrollIntoView?.({ block: 'nearest' });
    };
    text?.addEventListener('input', paint);
    if (text && typeof ResizeObserver === 'function') {
      // Wider or narrower re-wraps the words, and a pane that was hidden gets its size.
      let width = -1;
      new ResizeObserver(() => {
        if (text.offsetWidth === width) return;
        width = text.offsetWidth;
        paint();
      }).observe(text);
    }

    const LIVE_FAILURE_LABEL: Record<LiveFailure, string> = {
      blocked: 'blocked',
      'no-mic': 'no-mic',
      network: 'network',
      language: 'language',
      failed: 'live-stopped',
    };

    live = createLive(Recognition, {
      onInterim: (words) => {
        guess = words;
        paint();
      },
      onFinal: (words) => {
        if (!text) return;
        text.value = appendFinal(text.value, words);
        syncButtons();
        paint();
      },
      onSwitch: (on) => {
        listening = on;
        toggle?.setAttribute('aria-pressed', on ? 'true' : 'false');
        for (const span of qsa<HTMLElement>('[data-live-when]', toggle ?? livePane)) {
          span.hidden = span.dataset['liveWhen'] !== (on ? 'on' : 'off');
        }
        livePane.classList.toggle('is-listening', on);
        // A failure's own line follows this one (onFail), so a stop it caused says why.
        say(status, on ? 'listening' : 'live-stopped');
      },
      onFail: (failure) => say(status, LIVE_FAILURE_LABEL[failure]),
    });

    toggle?.addEventListener('click', () => {
      if (!live) return;
      if (live.on) {
        live.stop();
        return;
      }
      live.start(langSelect?.value ?? 'en-US');
      count('live_start', 'txt');
    });
    langSelect?.addEventListener('change', () => {
      if (live?.on) {
        live.stop();
        live.start(langSelect.value);
      }
    });
    copyBtn?.addEventListener('click', () => {
      const words = text?.value ?? '';
      if (!words.trim()) return;
      void copyText(words).then((ok) => {
        say(status, ok ? 'copied' : 'copy-failed');
        if (ok) count('live_copy', 'txt');
      });
    });
    // Words selected in the box and copied with the keyboard or the menu are live typing's text
    // copied too: one more `live_copy`, once per page view (lib/copies.ts).
    watchCopies([text], tool, () => count('live_copy', 'txt'));
    saveBtn?.addEventListener('click', () => {
      const words = text?.value ?? '';
      if (!words.trim()) return;
      // Made here, from the box: nothing is uploaded.
      saveBlob(new Blob([words], { type: 'text/plain;charset=utf-8' }), `${tool || 'speech'}.txt`);
      count('live_save', 'txt');
    });
    clearBtn?.addEventListener('click', () => {
      if (text) text.value = '';
      guess = '';
      syncButtons();
      paint();
      say(status, null);
      text?.focus();
    });
    syncButtons();
  }

  /* ── Record ────────────────────────────────────────────────────────── */

  const recPane = panes.get('record') ?? root;
  const startBtn = qs<HTMLButtonElement>('[data-rec-start]', recPane);
  const pauseBtn = qs<HTMLButtonElement>('[data-rec-pause]', recPane);
  const resumeBtn = qs<HTMLButtonElement>('[data-rec-resume]', recPane);
  const stopBtn = qs<HTMLButtonElement>('[data-rec-stop]', recPane);
  const timeText = qs<HTMLElement>('[data-rec-time]', recPane);
  const leftLine = qs<HTMLElement>('[data-rec-left]', recPane);
  const levelFill = qs<HTMLElement>('[data-rec-level-fill]', recPane);
  const levelBar = qs<HTMLElement>('[data-rec-level]', recPane);
  const recStatus = qs<HTMLElement>('[data-rec-status]', recPane);
  const wakeLine = qs<HTMLElement>('[data-rec-wake]', recPane);
  const player = qs<HTMLAudioElement>('[data-rec-player]', recPane);
  const actions = qs<HTMLElement>('[data-rec-actions]', recPane);
  const transcribeBtn = qs<HTMLButtonElement>('[data-rec-transcribe]', recPane);
  const downloadBtn = qs<HTMLButtonElement>('[data-rec-download]', recPane);

  let recording: Recording | null = null;
  let recordingName_ = '';
  let playerUrl: string | null = null;
  let sending = false;

  function dropRecording(): void {
    recording = null;
    if (playerUrl) URL.revokeObjectURL(playerUrl);
    playerUrl = null;
    if (player) {
      player.removeAttribute('src');
      player.hidden = true;
    }
    if (actions) actions.hidden = true;
  }

  const guardLeaving = (ev: BeforeUnloadEvent) => {
    ev.preventDefault();
    // Older engines show their own words only when this is set.
    ev.returnValue = '';
  };

  function paint(state: RecorderState): void {
    const active = state === 'recording' || state === 'paused';
    const busy = state === 'asking' || state === 'stopping';
    if (startBtn) {
      startBtn.hidden = active || state === 'stopping';
      startBtn.disabled = busy || sending;
      for (const span of qsa<HTMLElement>('[data-rec-when]', startBtn)) {
        span.hidden = span.dataset['recWhen'] !== (state === 'stopped' ? 'again' : 'new');
      }
    }
    if (pauseBtn) pauseBtn.hidden = state !== 'recording' || !recorder.canPause;
    if (resumeBtn) resumeBtn.hidden = state !== 'paused';
    if (stopBtn) stopBtn.hidden = !active;
    recPane.classList.toggle('is-recording', state === 'recording');
    if (levelBar) levelBar.classList.toggle('is-live', state === 'recording');
    // Live waits while the microphone is Record's.
    for (const tab of tabs) {
      if (tab.dataset['micTab'] === 'live') tab.disabled = active || busy;
    }
    if (active) window.addEventListener('beforeunload', guardLeaving);
    else window.removeEventListener('beforeunload', guardLeaving);
    if (state === 'asking') say(recStatus, 'asking');
    else if (state === 'recording') say(recStatus, 'recording');
    else if (state === 'paused') say(recStatus, 'paused');
    if (!active && wakeLine) wakeLine.hidden = true;
    if (state === 'idle' || state === 'asking') {
      if (leftLine) leftLine.hidden = true;
    }
  }

  let startCounted = false;
  const recorder = createRecorder(deps.recorderEnv ?? browserRecorderEnv(), {
    onState: (state) => {
      paint(state);
      if (state === 'recording' && !startCounted) {
        startCounted = true;
        count('record_start', recordingFormat(extensionFor(recorder.type)));
      }
    },
    onTick: (s) => {
      if (timeText) timeText.textContent = formatClock(s);
      if (leftLine) {
        const show = s >= WARN_FROM_S;
        leftLine.hidden = !show;
        const slot = qs<HTMLElement>('[data-slot="left"]', leftLine);
        if (show && slot) slot.textContent = formatClock(RECORD_CAP_S - s);
      }
    },
    onLevel: (level) => {
      if (levelFill) levelFill.style.transform = `scaleX(${level.toFixed(3)})`;
    },
    onWakeLock: (held) => {
      if (wakeLine) wakeLine.hidden = held;
    },
    onDone: (rec) => {
      recording = rec;
      recordingName_ = recordingName(rec.extension);
      if (playerUrl) URL.revokeObjectURL(playerUrl);
      playerUrl = URL.createObjectURL(rec.blob);
      if (player) {
        fixUnknownDuration(player);
        player.src = playerUrl;
        player.hidden = false;
      }
      if (actions) actions.hidden = false;
      if (transcribeBtn) transcribeBtn.disabled = false;
      if (downloadBtn) downloadBtn.disabled = false;
      if (timeText) timeText.textContent = formatClock(rec.seconds);
      say(recStatus, 'recorded', formatClock(rec.seconds));
    },
    onCapped: () => say(recStatus, 'capped'),
    onFail: (failure: RecorderFailure) => say(recStatus, failure),
  });

  startBtn?.addEventListener('click', () => {
    if (sending) return;
    dropRecording();
    startCounted = false;
    if (timeText) timeText.textContent = formatClock(0);
    void recorder.start();
  });
  pauseBtn?.addEventListener('click', () => recorder.pause());
  resumeBtn?.addEventListener('click', () => recorder.resume());
  stopBtn?.addEventListener('click', () => recorder.stop());

  downloadBtn?.addEventListener('click', () => {
    if (!recording) return;
    // Saved from this tab, in the format it was recorded in. Nothing is uploaded.
    saveBlob(recording.blob, recordingName_);
    count('record_download', recordingFormat(recording.extension));
  });

  transcribeBtn?.addEventListener('click', () => {
    const rec = recording;
    if (!rec || sending) return;
    sending = true;
    transcribeBtn.disabled = true;
    if (startBtn) startBtn.disabled = true;
    say(recStatus, 'sending');
    const file = new File([rec.blob], recordingName_, { type: rec.blob.type || rec.type });
    void deps
      .transcribe(file, rec.seconds, () =>
        count('record_transcribe', recordingFormat(rec.extension)),
      )
      .then((ok) => {
        if (ok) return;
        // Refused: the banner says why; the recording is still here to download or try again.
        sending = false;
        transcribeBtn.disabled = false;
        if (startBtn) startBtn.disabled = false;
        say(recStatus, 'recorded', formatClock(rec.seconds));
      });
  });

  paint(recorder.state);
  open(mode);

  return {
    open,
    get mode() {
      return mode;
    },
    recorder,
    get live() {
      return live;
    },
  };
}

/* ── small helpers ─────────────────────────────────────────────────────────── */

/**
 * Save a Blob from this tab with an anchor, as lib/download.ts saves a converted file. Live
 * captions' Download .txt (lib/live-captions.ts) saves with it too.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Chrome's recorder writes WebM with no length in it, so its player shows no duration and cannot
 * seek until it has played to the end. Asking for a time far past the end makes it read the file
 * through once and learn the length; then it goes back to the start.
 */
function fixUnknownDuration(player: HTMLAudioElement): void {
  const onMeta = () => {
    if (player.duration !== Number.POSITIVE_INFINITY) return;
    const back = () => {
      player.removeEventListener('timeupdate', back);
      player.currentTime = 0;
    };
    player.addEventListener('timeupdate', back);
    player.currentTime = 1e101;
  };
  player.addEventListener('loadedmetadata', onMeta, { once: true });
}
